<template>
  <div class="admin-users container py-4">
    <!-- Breadcrumbs -->
    <nav aria-label="breadcrumb" class="mb-3">
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <router-link to="/admin" class="text-decoration-none">
            <font-awesome-icon icon="home" class="me-1" />
            Admin
          </router-link>
        </li>
        <li class="breadcrumb-item active" aria-current="page">Benutzerverwaltung</li>
      </ol>
    </nav>

    <div class="container-fluid p-4" id="main-content">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 class="mb-0">
            <font-awesome-icon icon="users" class="me-2" />
            Benutzerverwaltung
          </h1>
          <p class="text-muted mb-0">
            Benutzer verwalten und überwachen
            <span v-if="teamFilter" class="badge bg-info ms-2">
              <font-awesome-icon icon="filter" class="me-1" />
              Gefiltert nach Bereich: {{ teamFilter.name }}
            </span>
          </p>
        </div>
        <div class="d-flex gap-2">
          <button
            type="button"
            class="btn btn-outline-primary"
            @click="confirmSyncAllUsers"
            :disabled="syncAllLoading"
          >
            <span
              v-if="syncAllLoading"
              class="spinner-border spinner-border-sm me-1"
              role="status"
              aria-hidden="true"
            ></span>
            <font-awesome-icon v-else icon="sync" class="me-1" />
            FluentCRM Sync
          </button>
          <BackButton />
        </div>
      </div>

      <!-- Search and Filters -->
      <div class="card shadow-sm mb-4">
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-4">
              <label for="search" class="form-label">Suche</label>
              <div class="input-group">
                <span class="input-group-text">
                  <font-awesome-icon icon="search" />
                </span>
                <input
                  v-model="searchQuery"
                  type="text"
                  class="form-control"
                  id="search"
                  placeholder="Nach Name oder E-Mail suchen..."
                  @input="debouncedSearch"
                />
              </div>
            </div>
            <div class="col-md-3">
              <label for="teamFilter" class="form-label">Bereich-Filter</label>
              <multiselect
                v-model="teamFilter"
                :options="teamOptions"
                :searchable="true"
                :close-on-select="true"
                :use-teleport="true"
                :show-labels="false"
                placeholder="Bereich auswählen..."
                label="name"
                track-by="id"
                :no-options="'Keine Bereiche verfügbar'"
                :no-results="'Keine Bereiche gefunden'"
                @select="updateTeamFilter"
                @remove="updateTeamFilter"
              />
            </div>
            <div class="col-md-2">
              <label for="itemsPerPage" class="form-label">Einträge pro Seite</label>
              <multiselect
                v-model="itemsPerPage"
                :options="itemsPerPageOptions"
                :searchable="true"
                :close-on-select="true"
                :use-teleport="true"
                :show-labels="false"
                placeholder="Einträge pro Seite..."
                label="label"
                track-by="value"
                :no-options="'Keine Optionen verfügbar'"
                :no-results="'Keine Optionen gefunden'"
                @select="loadUsers"
              />
            </div>
            <div class="col-md-3 d-flex align-items-end gap-2">
              <button 
                v-if="teamFilter"
                class="btn btn-outline-warning btn-sm" 
                @click="clearTeamFilter"
                title="Bereich-Filter zurücksetzen"
              >
                <font-awesome-icon icon="times" class="me-1" />
                Filter zurücksetzen
              </button>
              <button 
                class="btn btn-outline-secondary" 
                @click="showTableSettingsModal"
                title="Tabellen-Einstellungen"
              >
                <font-awesome-icon icon="table" class="me-1" />
                Spalten
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Users Table -->
      <div class="card shadow-sm">
        <div class="card-body">
          <!-- Loading -->
          <div v-if="loading" class="text-center my-5">
            <div class="spinner-border" role="status">
              <span class="visually-hidden">Benutzer werden geladen...</span>
            </div>
          </div>

          <!-- Error -->
          <div v-else-if="error" class="alert alert-danger" role="alert">
            <font-awesome-icon icon="exclamation-triangle" class="me-2" />
            {{ error }}
          </div>

          <!-- Users Table -->
          <div v-else>
            <div class="table-responsive">
              <table class="table table-hover">
                <thead>
                  <tr>
                    <th v-if="visibleColumns.includes('id')">
                      <button class="btn btn-link p-0 text-decoration-none fw-bold" @click="toggleSort('id')">
                        ID
                        <font-awesome-icon 
                          v-if="sortField === 'id'" 
                          :icon="sortDirection === 'asc' ? 'sort-up' : 'sort-down'" 
                          class="ms-1"
                        />
                        <font-awesome-icon v-else icon="sort" class="ms-1 text-muted" />
                      </button>
                    </th>
                    <th v-if="visibleColumns.includes('name')">
                      <button class="btn btn-link p-0 text-decoration-none fw-bold" @click="toggleSort('name')">
                        Name
                        <font-awesome-icon 
                          v-if="sortField === 'name'" 
                          :icon="sortDirection === 'asc' ? 'sort-up' : 'sort-down'" 
                          class="ms-1"
                        />
                        <font-awesome-icon v-else icon="sort" class="ms-1 text-muted" />
                      </button>
                    </th>
                    <th v-if="visibleColumns.includes('email')">
                      <button class="btn btn-link p-0 text-decoration-none fw-bold" @click="toggleSort('email')">
                        E-Mail
                        <font-awesome-icon 
                          v-if="sortField === 'email'" 
                          :icon="sortDirection === 'asc' ? 'sort-up' : 'sort-down'" 
                          class="ms-1"
                        />
                        <font-awesome-icon v-else icon="sort" class="ms-1 text-muted" />
                      </button>
                    </th>
                    <th v-if="visibleColumns.includes('verified')">
                      <button class="btn btn-link p-0 text-decoration-none fw-bold" @click="toggleSort('verified')">
                        Verifiziert
                        <font-awesome-icon 
                          v-if="sortField === 'verified'" 
                          :icon="sortDirection === 'asc' ? 'sort-up' : 'sort-down'" 
                          class="ms-1"
                        />
                        <font-awesome-icon v-else icon="sort" class="ms-1 text-muted" />
                      </button>
                    </th>
                    <th v-if="visibleColumns.includes('scheduledDeletionAt')">
                      <button
                        class="btn btn-link p-0 text-decoration-none fw-bold"
                        @click="toggleSort('scheduledDeletionAt')"
                      >
                        Löschung
                        <font-awesome-icon
                          v-if="sortField === 'scheduledDeletionAt'"
                          :icon="sortDirection === 'asc' ? 'sort-up' : 'sort-down'"
                          class="ms-1"
                        />
                        <font-awesome-icon v-else icon="sort" class="ms-1 text-muted" />
                      </button>
                    </th>
                    <th v-if="visibleColumns.includes('account')">
                      <button class="btn btn-link p-0 text-decoration-none fw-bold" @click="toggleSort('account')">
                        Kontostand
                        <font-awesome-icon 
                          v-if="sortField === 'account'" 
                          :icon="sortDirection === 'asc' ? 'sort-up' : 'sort-down'" 
                          class="ms-1"
                        />
                        <font-awesome-icon v-else icon="sort" class="ms-1 text-muted" />
                      </button>
                    </th>
                    <th v-if="visibleColumns.includes('lastLoginAt')">
                      <button class="btn btn-link p-0 text-decoration-none fw-bold" @click="toggleSort('lastLoginAt')">
                        Letzter Login
                        <font-awesome-icon 
                          v-if="sortField === 'lastLoginAt'" 
                          :icon="sortDirection === 'asc' ? 'sort-up' : 'sort-down'" 
                          class="ms-1"
                        />
                        <font-awesome-icon v-else icon="sort" class="ms-1 text-muted" />
                      </button>
                    </th>
                    <th v-if="visibleColumns.includes('lastActivityAt')">
                      <button class="btn btn-link p-0 text-decoration-none fw-bold" @click="toggleSort('lastActivityAt')">
                        Letzte Aktivität
                        <font-awesome-icon 
                          v-if="sortField === 'lastActivityAt'" 
                          :icon="sortDirection === 'asc' ? 'sort-up' : 'sort-down'" 
                          class="ms-1"
                        />
                        <font-awesome-icon v-else icon="sort" class="ms-1 text-muted" />
                      </button>
                    </th>
                    <th v-if="visibleColumns.includes('createdAt')">
                      <button class="btn btn-link p-0 text-decoration-none fw-bold" @click="toggleSort('createdAt')">
                        Erstellt
                        <font-awesome-icon 
                          v-if="sortField === 'createdAt'" 
                          :icon="sortDirection === 'asc' ? 'sort-up' : 'sort-down'" 
                          class="ms-1"
                        />
                        <font-awesome-icon v-else icon="sort" class="ms-1 text-muted" />
                      </button>
                    </th>
                    <th class="fw-bold">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="user in users" :key="user.id">
                    <td v-if="visibleColumns.includes('id')">
                      <code class="small">{{ user.id }}</code>
                    </td>
                    <td v-if="visibleColumns.includes('name')">
                      <strong>{{ user.firstName }} {{ user.lastName }}</strong>
                    </td>
                    <td v-if="visibleColumns.includes('email')">{{ user.email }}</td>
                    <td v-if="visibleColumns.includes('verified')">
                      <span v-if="user.verified" class="badge bg-success">
                        <font-awesome-icon icon="check" class="me-1" />
                        Verifiziert
                      </span>
                      <span v-else class="badge bg-warning">
                        <font-awesome-icon icon="times" class="me-1" />
                        Nicht verifiziert
                      </span>
                    </td>
                    <td v-if="visibleColumns.includes('scheduledDeletionAt')">
                      <div
                        v-if="user.lifecycleStatus === 'deletion_scheduled' && user.scheduledDeletionAt"
                        class="deletion-status"
                      >
                        <span class="badge bg-danger mb-1">
                          <font-awesome-icon icon="clock" class="me-1" />
                          Vorgemerkt
                        </span>
                        <div class="small text-danger fw-semibold">
                          {{ formatDate(user.scheduledDeletionAt) }} Uhr
                        </div>
                      </div>
                      <span v-else class="text-muted">–</span>
                    </td>
                    <td v-if="visibleColumns.includes('account')">{{ user?.account?.amount }}</td>
                    <td v-if="visibleColumns.includes('lastLoginAt')">
                      <span 
                        class="time-display" 
                        :title="getDetailedTimestamp(user.lastLoginAt)"
                        @click="toggleDetailedTimeInCell($event, user.lastLoginAt)"
                      >
                        {{ getRelativeTime(user.lastLoginAt) }}
                      </span>
                    </td>
                    <td v-if="visibleColumns.includes('lastActivityAt')">
                      <span 
                        class="time-display" 
                        :title="getDetailedTimestamp(user.lastActivityAt)"
                        @click="toggleDetailedTimeInCell($event, user.lastActivityAt)"
                      >
                        {{ getRelativeTime(user.lastActivityAt) }}
                      </span>
                    </td>
                    <td v-if="visibleColumns.includes('createdAt')">
                      <span 
                        class="time-display" 
                        :title="getDetailedTimestamp(user.createdAt)"
                        @click="toggleDetailedTimeInCell($event, user.createdAt)"
                      >
                        {{ getRelativeTime(user.createdAt) }}
                      </span>
                    </td>
                    <td>
                      <div class="btn-group" role="group">
                        <button
                          type="button"
                          class="btn btn-outline-info btn-sm"
                          @click="showUserTeams(user)"
                          title="Bereiche anzeigen"
                        >
                          <font-awesome-icon icon="eye" />
                        </button>
                        <button
                          type="button"
                          class="btn btn-outline-info btn-sm"
                          @click="showUserHistory(user)"
                          title="Historie anzeigen"
                        >
                          <font-awesome-icon icon="history" />
                        </button>
                        <button
                          type="button"
                          class="btn btn-outline-warning btn-sm"
                          @click="confirmImpersonate(user)"
                          title="Als Benutzer einloggen"
                        >
                          <font-awesome-icon icon="user-secret" />
                        </button>
                        <button
                          type="button"
                          class="btn btn-outline-danger btn-sm"
                          :disabled="deletingUserId === user.id || authStore.user?.id === user.id"
                          @click="confirmDeleteUser(user)"
                          :title="authStore.user?.id === user.id
                            ? 'Das eigene Administratorkonto kann hier nicht gelöscht werden'
                            : 'Benutzer löschen'"
                        >
                          <span
                            v-if="deletingUserId === user.id"
                            class="spinner-border spinner-border-sm"
                            aria-hidden="true"
                          ></span>
                          <font-awesome-icon v-else icon="trash" />
                          <span class="visually-hidden">Benutzer löschen</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <nav v-if="pagination.totalPages > 1" aria-label="Benutzer-Navigation">
              <ul class="pagination justify-content-center">
                <li class="page-item" :class="{ disabled: pagination.currentPage === 1 }">
                  <button class="page-link" @click="changePage(pagination.currentPage - 1)" :disabled="pagination.currentPage === 1">
                    <font-awesome-icon icon="chevron-left" />
                  </button>
                </li>
                
                <li v-for="page in visiblePages" :key="page" class="page-item" :class="{ active: page === pagination.currentPage }">
                  <button class="page-link" @click="changePage(page)">{{ page }}</button>
                </li>
                
                <li class="page-item" :class="{ disabled: pagination.currentPage === pagination.totalPages }">
                  <button class="page-link" @click="changePage(pagination.currentPage + 1)" :disabled="pagination.currentPage === pagination.totalPages">
                    <font-awesome-icon icon="chevron-right" />
                  </button>
                </li>
              </ul>
            </nav>

            <!-- Pagination Info -->
            <div class="text-center text-muted mt-3">
              Zeige {{ paginationInfo }} von {{ pagination.totalItems }} Benutzern
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Teams Modal -->
    <Teleport to="#modalTarget">
      <div v-if="showTeamsModal" class="modal-backdrop fade show"></div>
      <div v-if="showTeamsModal" class="modal show d-block" tabindex="-1" role="dialog">
        <div class="modal-dialog modal-lg" role="document">
          <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Bereiche von {{ selectedUser?.firstName }} {{ selectedUser?.lastName }}
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="closeTeamsModal"
              aria-label="Schließen"
            ></button>
          </div>
          <div class="modal-body">
            <!-- Teams Search -->
            <div v-if="!teamsLoading && !teamsError" class="mb-3">
              <div class="row g-2">
                <div class="col-md-8">
                  <div class="input-group">
                    <span class="input-group-text">
                      <font-awesome-icon icon="search" />
                    </span>
                    <input
                      v-model="teamsSearchQuery"
                      type="text"
                      class="form-control"
                      placeholder="Nach Bereich-Name oder Beschreibung suchen..."
                      @input="debouncedTeamsSearch"
                    />
                  </div>
                </div>
                <div class="col-md-4">
                  <multiselect
                    v-model="teamsItemsPerPage"
                    :options="teamsItemsPerPageOptions"
                    :searchable="true"
                    :close-on-select="true"
                    :use-teleport="true"
                    :show-labels="false"
                    placeholder="Einträge pro Seite..."
                    label="label"
                    track-by="value"
                    :no-options="'Keine Optionen verfügbar'"
                    :no-results="'Keine Optionen gefunden'"
                    @select="loadUserTeams"
                  />
                </div>
              </div>
            </div>

            <div v-if="teamsLoading" class="text-center">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">Bereiche werden geladen...</span>
              </div>
            </div>
            <div v-else-if="teamsError" class="alert alert-danger">
              {{ teamsError }}
            </div>
            <div v-else-if="userTeams.length === 0" class="text-center text-muted">
              <font-awesome-icon icon="users" class="fa-3x mb-3" />
              <p v-if="teamsSearchQuery">Keine Bereiche gefunden.</p>
              <p v-else>Dieser Benutzer ist noch keinem Bereich beigetreten.</p>
            </div>
            <div v-else>
              <div class="table-responsive">
                <table class="table">
                  <thead>
                    <tr>
                      <th>Bereich Name</th>
                      <th>Rolle</th>
                      <th>Beigetreten</th>
                      <th>Erstellt</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="team in userTeams" :key="team.id">
                      <td>
                        <strong>{{ team.name }}</strong>
                        <div v-if="team.description" class="small text-muted">
                          {{ team.description }}
                        </div>
                        <div class="small text-muted">
                          <code>{{ team.id }}</code>
                        </div>
                      </td>
                      <td>
                        <span class="badge" :class="getRoleBadgeClass(team.role)">
                          {{ getRoleLabel(team.role) }}
                        </span>
                      </td>
                      <td>{{ formatDate(team.joinedAt) }}</td>
                      <td>{{ formatDate(team.createdAt) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Teams Pagination -->
              <nav v-if="teamsPagination.totalPages > 1" aria-label="Bereiche-Navigation" class="mt-3">
                <ul class="pagination justify-content-center">
                  <li class="page-item" :class="{ disabled: teamsPagination.currentPage === 1 }">
                    <button class="page-link" @click="changeTeamsPage(teamsPagination.currentPage - 1)" :disabled="teamsPagination.currentPage === 1">
                      <font-awesome-icon icon="chevron-left" />
                    </button>
                  </li>
                  
                  <li v-for="page in visibleTeamsPages" :key="page" class="page-item" :class="{ active: page === teamsPagination.currentPage }">
                    <button class="page-link" @click="changeTeamsPage(page)">{{ page }}</button>
                  </li>
                  
                  <li class="page-item" :class="{ disabled: teamsPagination.currentPage === teamsPagination.totalPages }">
                    <button class="page-link" @click="changeTeamsPage(teamsPagination.currentPage + 1)" :disabled="teamsPagination.currentPage === teamsPagination.totalPages">
                      <font-awesome-icon icon="chevron-right" />
                    </button>
                  </li>
                </ul>
              </nav>

              <!-- Teams Pagination Info -->
              <div class="text-center text-muted mt-2">
                Zeige {{ teamsPaginationInfo }} von {{ teamsPagination.totalItems }} Bereichen
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeTeamsModal">
              Schließen
            </button>
          </div>
        </div>
      </div>
    </div>    </Teleport>

    <!-- Table Settings Modal -->
    <Teleport to="#modalTarget">
      <div v-if="showTableModal" class="modal-backdrop fade show"></div>
      <div v-if="showTableModal" class="modal show d-block" tabindex="-1" role="dialog">
        <div class="modal-dialog modal-lg" role="document">
          <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <font-awesome-icon icon="table" class="me-2" />
              Tabellen-Einstellungen
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="closeTableModal"
              aria-label="Schließen"
            ></button>
          </div>
          <div class="modal-body">
            <div class="row">
              <div class="col-12">
                <h6 class="mb-3">Sichtbare Spalten:</h6>
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-check">
                      <input 
                        class="form-check-input" 
                        type="checkbox" 
                        id="table-col-id" 
                        v-model="visibleColumns" 
                        value="id"
                      >
                      <label class="form-check-label" for="table-col-id">ID</label>
                    </div>
                    <div class="form-check">
                      <input 
                        class="form-check-input" 
                        type="checkbox" 
                        id="table-col-name" 
                        v-model="visibleColumns" 
                        value="name"
                      >
                      <label class="form-check-label" for="table-col-name">Name</label>
                    </div>
                    <div class="form-check">
                      <input 
                        class="form-check-input" 
                        type="checkbox" 
                        id="table-col-email" 
                        v-model="visibleColumns" 
                        value="email"
                      >
                      <label class="form-check-label" for="table-col-email">E-Mail</label>
                    </div>
                    <div class="form-check">
                      <input 
                        class="form-check-input" 
                        type="checkbox" 
                        id="table-col-verified" 
                        v-model="visibleColumns" 
                        value="verified"
                      >
                      <label class="form-check-label" for="table-col-verified">Verifiziert</label>
                    </div>
                    <div class="form-check">
                      <input
                        id="table-col-scheduledDeletionAt"
                        v-model="visibleColumns"
                        class="form-check-input"
                        type="checkbox"
                        value="scheduledDeletionAt"
                      >
                      <label class="form-check-label" for="table-col-scheduledDeletionAt">
                        Löschung
                      </label>
                    </div>
                    <div class="form-check">
                      <input 
                        class="form-check-input" 
                        type="checkbox" 
                        id="table-col-account" 
                        v-model="visibleColumns" 
                        value="account"
                      >
                      <label class="form-check-label" for="table-col-account">Kontostand</label>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-check">
                      <input 
                        class="form-check-input" 
                        type="checkbox" 
                        id="table-col-lastLoginAt" 
                        v-model="visibleColumns" 
                        value="lastLoginAt"
                      >
                      <label class="form-check-label" for="table-col-lastLoginAt">Letzter Login</label>
                    </div>
                    <div class="form-check">
                      <input 
                        class="form-check-input" 
                        type="checkbox" 
                        id="table-col-lastActivityAt" 
                        v-model="visibleColumns" 
                        value="lastActivityAt"
                      >
                      <label class="form-check-label" for="table-col-lastActivityAt">Letzte Aktivität</label>
                    </div>
                    <div class="form-check">
                      <input 
                        class="form-check-input" 
                        type="checkbox" 
                        id="table-col-createdAt" 
                        v-model="visibleColumns" 
                        value="createdAt"
                      >
                      <label class="form-check-label" for="table-col-createdAt">Erstellt</label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="mt-4">
              <div class="d-flex gap-2">
                <button 
                  class="btn btn-outline-primary" 
                  @click="selectAllColumns"
                >
                  <font-awesome-icon icon="check-square" class="me-2" />
                  Alle auswählen
                </button>
                <button 
                  class="btn btn-outline-secondary" 
                  @click="resetColumns"
                >
                  <font-awesome-icon icon="square" class="me-2" />
                  Standard-Spalten
                </button>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeTableModal">
              Schließen
            </button>
            <button type="button" class="btn btn-primary" @click="closeTableModal">
              Übernehmen
            </button>
          </div>
        </div>
      </div>
    </div>
 </Teleport>
</div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { adminApi } from '@/api/admin'
import BackButton from '@/components/common/BackButton.vue'
import { getRelativeTime, getDetailedTimestamp } from '@/utils/timeUtils'
import Swal from 'sweetalert2'
import Multiselect from 'vue-multiselect'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()


const users = ref([])
const loading = ref(false)
const error = ref(null)
const searchQuery = ref('')
const itemsPerPage = ref({ value: 20, label: '20' })
const teamFilter = ref(null)
const teamOptions = ref([])
const syncAllLoading = ref(false)
const deletingUserId = ref(null)

// Options for multiselects
const itemsPerPageOptions = [
  { value: 10, label: '10' },
  { value: 20, label: '20' },
  { value: 50, label: '50' },
  { value: 100, label: '100' }
]

const teamsItemsPerPageOptions = [
  { value: 5, label: '5 pro Seite' },
  { value: 10, label: '10 pro Seite' },
  { value: 20, label: '20 pro Seite' }
]
const currentPage = ref(1)
const pagination = ref({
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
  itemsPerPage: 20
})

// Table settings
const visibleColumns = ref([
  'name',
  'email',
  'verified',
  'scheduledDeletionAt',
  'lastLoginAt',
  'lastActivityAt',
  'createdAt'
])
const showTableModal = ref(false)
const sortField = ref('createdAt')
const sortDirection = ref('desc')


const showTeamsModal = ref(false)
const selectedUser = ref(null)
const userTeams = ref([])
const teamsLoading = ref(false)
const teamsError = ref(null)
const teamsSearchQuery = ref('')
const teamsItemsPerPage = ref({ value: 10, label: '10 pro Seite' })
const teamsCurrentPage = ref(1)
const teamsPagination = ref({
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
  itemsPerPage: 10
})


const paginationInfo = computed(() => {
  const start = (pagination.value.currentPage - 1) * pagination.value.itemsPerPage + 1
  const end = Math.min(start + pagination.value.itemsPerPage - 1, pagination.value.totalItems)
  return `${start}-${end}`
})

const visiblePages = computed(() => {
  const pages = []
  const current = pagination.value.currentPage
  const total = pagination.value.totalPages
  
  
  let start = Math.max(1, current - 2)
  let end = Math.min(total, current + 2)
  
  
  if (end - start < 4) {
    if (start === 1) {
      end = Math.min(total, start + 4)
    } else {
      start = Math.max(1, end - 4)
    }
  }
  
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  
  return pages
})


const teamsPaginationInfo = computed(() => {
  const start = (teamsPagination.value.currentPage - 1) * teamsPagination.value.itemsPerPage + 1
  const end = Math.min(start + teamsPagination.value.itemsPerPage - 1, teamsPagination.value.totalItems)
  return `${start}-${end}`
})

const visibleTeamsPages = computed(() => {
  const pages = []
  const current = teamsPagination.value.currentPage
  const total = teamsPagination.value.totalPages
  
  let start = Math.max(1, current - 2)
  let end = Math.min(total, current + 2)
  
  if (end - start < 4) {
    if (start === 1) {
      end = Math.min(total, start + 4)
    } else {
      start = Math.max(1, end - 4)
    }
  }
  
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  
  return pages
})


const loadUsers = async () => {
  loading.value = true
  error.value = null
  
  try {
    const params = {
      page: currentPage.value,
      limit: itemsPerPage.value?.value || 20,
      search: searchQuery.value.trim(),
      sortField: sortField.value,
      sortDirection: sortDirection.value,
      teamId: teamFilter.value?.id || ''
    }
    
    const response = await adminApi.getAllUsers(params)
    users.value = response.users
    pagination.value = response.pagination
  } catch (err) {
    console.error('Error loading users:', err)
    error.value = err.response?.data?.message || 'Fehler beim Laden der Benutzer'
  } finally {
    loading.value = false
  }
}

const loadTeams = async () => {
  try {
    const response = await adminApi.getAllTeams({ limit: 1000 })
    teamOptions.value = response.teams.map(team => ({
      id: team.id,
      name: team.name
    }))
  } catch (err) {
    console.error('Error loading teams:', err)
  }
}

const updateTeamFilter = (team) => {
  teamFilter.value = team
  currentPage.value = 1
  
  // URL aktualisieren
  const newQuery = { ...route.query }
  if (team) {
    newQuery.teamId = team.id
  } else {
    delete newQuery.teamId
  }
  
  router.push({ query: newQuery })
  loadUsers()
}

const clearTeamFilter = () => {
  updateTeamFilter(null)
}

const debouncedSearch = () => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    currentPage.value = 1
    loadUsers()
  }, 300)
}

const changePage = (page) => {
  if (page >= 1 && page <= pagination.value.totalPages) {
    currentPage.value = page
    loadUsers()
  }
}

const showUserTeams = async (user) => {
  selectedUser.value = user
  showTeamsModal.value = true
  teamsSearchQuery.value = ''
  teamsCurrentPage.value = 1
  teamsItemsPerPage.value = 10
  await loadUserTeams()
}

const showUserHistory = (user) => {
  router.push(`/admin/users/${user.id}/history`)
}

const loadUserTeams = async () => {
  if (!selectedUser.value) return
  
  teamsLoading.value = true
  teamsError.value = null
  userTeams.value = []
  
  try {
    const params = {
      page: teamsCurrentPage.value,
      limit: teamsItemsPerPage.value?.value || 10,
      search: teamsSearchQuery.value.trim()
    }
    
    const response = await adminApi.getUserTeams(selectedUser.value.id, params)
    userTeams.value = response.teams
    teamsPagination.value = response.pagination
  } catch (err) {
    console.error('Error loading user teams:', err)
    teamsError.value = err.response?.data?.message || 'Fehler beim Laden der Bereiche'
  } finally {
    teamsLoading.value = false
  }
}

const debouncedTeamsSearch = () => {
  clearTimeout(teamsSearchTimeout)
  teamsSearchTimeout = setTimeout(() => {
    teamsCurrentPage.value = 1
    loadUserTeams()
  }, 300)
}

const changeTeamsPage = (page) => {
  if (page >= 1 && page <= teamsPagination.value.totalPages) {
    teamsCurrentPage.value = page
    loadUserTeams()
  }
}

const closeTeamsModal = () => {
  showTeamsModal.value = false
  selectedUser.value = null
  userTeams.value = []
  teamsError.value = null
  teamsSearchQuery.value = ''
  teamsCurrentPage.value = 1
  teamsPagination.value = {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  }
}

const confirmImpersonate = async (user) => {
  try {
    const result = await Swal.fire({
      title: 'Als Benutzer einloggen?',
      text: `Möchtest du dich wirklich als "${user.firstName} ${user.lastName}" (${user.email}) einloggen?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Ja, einloggen',
      cancelButtonText: 'Abbrechen'
    })

    if (result.isConfirmed) {
      await impersonateUser(user)
    }
  } catch (err) {
    console.error('Error during impersonation confirmation:', err)
  }
}

const confirmDeleteUser = async (user) => {
  if (authStore.user?.id === user.id) return

  const displayName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email
  const result = await Swal.fire({
    title: 'Benutzer endgültig löschen?',
    text: `${displayName} (${user.email}) wird gelöscht. Persönliche Quittungen und selbst erstellte Bereiche können dabei ebenfalls gelöscht werden. Diese Aktion kann nicht rückgängig gemacht werden.`,
    icon: 'warning',
    input: 'email',
    inputLabel: `Zur Bestätigung bitte ${user.email} eingeben:`,
    inputPlaceholder: user.email,
    inputAttributes: {
      autocomplete: 'off',
      autocapitalize: 'off',
      spellcheck: 'false'
    },
    showCancelButton: true,
    confirmButtonColor: '#dc3545',
    confirmButtonText: 'Endgültig löschen',
    cancelButtonText: 'Abbrechen',
    showLoaderOnConfirm: true,
    inputValidator: (value) => {
      if (String(value || '').trim().toLowerCase() !== user.email.toLowerCase()) {
        return 'Die E-Mail-Adresse stimmt nicht überein.'
      }
      return undefined
    },
    preConfirm: async (confirmation) => {
      deletingUserId.value = user.id
      try {
        return await adminApi.deleteUser(user.id, String(confirmation).trim())
      } catch (err) {
        Swal.showValidationMessage(
          err.response?.data?.message || 'Der Benutzer konnte nicht gelöscht werden.'
        )
        return false
      } finally {
        deletingUserId.value = null
      }
    },
    allowOutsideClick: () => !Swal.isLoading()
  })

  if (!result.isConfirmed) return

  if (users.value.length === 1 && currentPage.value > 1) {
    currentPage.value -= 1
  }
  await loadUsers()
  await Swal.fire({
    title: 'Benutzer gelöscht',
    text: result.value?.message || 'Der Benutzer wurde erfolgreich gelöscht.',
    icon: 'success'
  })
}

const confirmSyncAllUsers = async () => {
  try {
    const result = await Swal.fire({
      title: 'FluentCRM Sync starten?',
      text: 'Alle Benutzer werden in FluentCRM synchronisiert.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ja, starten',
      cancelButtonText: 'Abbrechen'
    })

    if (!result.isConfirmed) {
      return
    }

    syncAllLoading.value = true
    const response = await adminApi.syncFluentcrmUsers()
    const isSkipped = response?.skipped
    await Swal.fire({
      title: isSkipped ? 'Uebersprungen' : 'Gestartet',
      text: response?.message || 'FluentCRM Sync gestartet',
      icon: isSkipped ? 'info' : 'success'
    })
  } catch (err) {
    console.error('Error syncing FluentCRM users:', err)
    await Swal.fire({
      title: 'Fehler',
      text: err.response?.data?.message || 'Fehler beim FluentCRM Sync',
      icon: 'error'
    })
  } finally {
    syncAllLoading.value = false
  }
}

const impersonateUser = async (user) => {
  try {
    const response = await adminApi.impersonateUser(user.id)
    
    
    authStore.setImpersonatedTokens(response.tokens, response.user)
    
    
    await Swal.fire({
      title: 'Erfolgreich eingeloggt!',
      text: `Du bist jetzt als "${user.firstName} ${user.lastName}" eingeloggt.`,
      icon: 'success',
      timer: 500,
      showConfirmButton: false
    })
    
    
    router.push('/dashboard')
  } catch (err) {
    console.error('Error impersonating user:', err)
    await Swal.fire({
      title: 'Fehler',
      text: err.response?.data?.message || 'Fehler beim Einloggen als Benutzer',
      icon: 'error'
    })
  }
}

const formatDate = (dateString) => {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleDateString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getRoleLabel = (role) => {
  const roleLabels = {
    'admin': 'Administrator',
    'member': 'Mitglied',
    'creator': 'Ersteller'
  }
  return roleLabels[role] || role
}

const getRoleBadgeClass = (role) => {
  const badgeClasses = {
    'admin': 'bg-danger',
    'member': 'bg-secondary',
    'creator': 'bg-primary'
  }
  return badgeClasses[role] || 'bg-secondary'
}


// Table functions
const showTableSettingsModal = () => {
  showTableModal.value = true
}

const closeTableModal = () => {
  showTableModal.value = false
}

const selectAllColumns = () => {
  visibleColumns.value = [
    'id',
    'name',
    'email',
    'verified',
    'scheduledDeletionAt',
    'account',
    'lastLoginAt',
    'lastActivityAt',
    'createdAt'
  ]
}

const resetColumns = () => {
  visibleColumns.value = [
    'name',
    'email',
    'verified',
    'scheduledDeletionAt',
    'lastLoginAt',
    'lastActivityAt',
    'createdAt'
  ]
}

const toggleSort = (field) => {
  if (sortField.value === field) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortField.value = field
    sortDirection.value = 'asc'
  }
  loadUsers()
}

const toggleDetailedTimeInCell = (event, dateString) => {
  if (!dateString) return
  
  const element = event.target
  const isShowingDetailed = element.classList.contains('detailed-time')
  
  if (isShowingDetailed) {
    // Hide detailed time, show relative time
    const originalText = element.dataset.originalText
    if (originalText) {
      element.textContent = originalText
      element.classList.remove('detailed-time')
    }
  } else {
    // Show detailed time
    const originalText = element.textContent
    const detailedTime = getDetailedTimestamp(dateString)
    
    // Store original text for restoration
    element.dataset.originalText = originalText
    
    // Show detailed time
    element.textContent = detailedTime
    element.classList.add('detailed-time')
  }
}

let searchTimeout = null
let teamsSearchTimeout = null

const initializeTeamFilter = () => {
  const teamId = route.query.teamId
  if (teamId) {
    const team = teamOptions.value.find(t => t.id === teamId)
    if (team) {
      teamFilter.value = team
    } else if (teamOptions.value.length === 0) {
      // Wenn Teams noch nicht geladen sind, warten wir kurz und versuchen es erneut
      setTimeout(() => {
        const team = teamOptions.value.find(t => t.id === teamId)
        if (team) {
          teamFilter.value = team
        }
      }, 100)
    }
  }
}

// Watcher für Route-Änderungen
watch(() => route.query.teamId, (newTeamId) => {
  if (newTeamId && teamOptions.value.length > 0) {
    const team = teamOptions.value.find(t => t.id === newTeamId)
    if (team && teamFilter.value?.id !== team.id) {
      teamFilter.value = team
      loadUsers()
    }
  } else if (!newTeamId && teamFilter.value) {
    teamFilter.value = null
    loadUsers()
  }
})

onMounted(async () => {
  await loadTeams()
  initializeTeamFilter()
  loadUsers()
})
</script>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>

<style scoped>
.time-display {
  color: #0d6efd;
  text-decoration: underline;
  text-decoration-style: dotted;
  cursor: pointer;
  transition: all 0.2s ease;
}

.time-display:hover {
  color: #0b5ed7;
  text-decoration-style: solid;
  background-color: #f8f9fa;
  padding: 2px 4px;
  border-radius: 3px;
}

.detailed-time {
  color: #495057 !important;
  background-color: #e9ecef !important;
  font-size: 0.85em;
  font-family: monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
  display: inline-block;
}

.deletion-status {
  min-width: 145px;
  white-space: nowrap;
}

.table th button {
  border: none;
  background: none;
  padding: 0;
  color: inherit;
  font-weight: inherit;
}

.table th button:hover {
  color: #0d6efd;
}

.table th button:focus {
  outline: none;
  box-shadow: none;
}
</style>
